import { useEffect, useMemo, useState } from "react"

import readXlsxFile, { type CellValue, type Row } from "read-excel-file/browser"

import type { SpreadsheetData } from "../components/spreadsheet-viewer"

type UseReadSpreadsheetDataProps = {
  files: File[]
  chatIndex: number
  sessionId?: string | null
}

export function useReadSpreadsheetData({
  files,
  chatIndex,
  sessionId,
}: UseReadSpreadsheetDataProps) {
  const [spreadSheetData, setSpreadSheetData] = useState<SpreadsheetData>([])
  const [spreadSheetChatIndex, setSpreadSheetChatIndex] = useState(0)
  const [loadedSessionId, setLoadedSessionId] = useState<string | null | undefined>(null)

  const latestSpreadsheetFile = useMemo(() => {
    return files.findLast(
      (file) =>
        file.type === "application/vnd.ms-excel" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "text/csv"
    )
  }, [files])

  // Clear spreadsheet data when session changes
  useEffect(() => {
    setSpreadSheetData([])
    setSpreadSheetChatIndex(0)
    setLoadedSessionId(null)
  }, [sessionId])

  // Read spreadsheet file only when the file itself changes
  useEffect(() => {
    if (!latestSpreadsheetFile) {
      setSpreadSheetData([])
      setSpreadSheetChatIndex(0)
      setLoadedSessionId(null)
      return
    }
    // Only read the file if it's for the current session
    if (sessionId !== loadedSessionId) {
      const readExcelFile = async (file: File) => {
        const buffer = await file.arrayBuffer()
        const sheets = await readXlsxFile(buffer)
        const rows = sheets[0]?.data ?? []
        const formattedData = rows.slice(0, 200).map((row: Row) =>
          row.slice(0, 50).map((cell: CellValue | null) => ({
            value: cell ?? "",
            readOnly: true,
          }))
        )
        setSpreadSheetData(formattedData)
        setSpreadSheetChatIndex(chatIndex)
        setLoadedSessionId(sessionId)
      }
      void readExcelFile(latestSpreadsheetFile)
    }
  }, [latestSpreadsheetFile, sessionId, loadedSessionId, chatIndex])

  // Update chat index separately when it changes
  useEffect(() => {
    if (spreadSheetData.length > 0) {
      setSpreadSheetChatIndex(chatIndex)
    }
  }, [chatIndex, spreadSheetData.length])

  return { spreadSheetData, spreadSheetChatIndex }
}
